/**
 * SIL-1348 - the Active Verification state chip. Small and self-contained on
 * purpose: the marketplace card (SIL-1347) and the node Passport drawer
 * (SIL-1349) both need this same three-state decision, and a second
 * implementation is how one of them ends up showing the Verified mark for an
 * aged-out run.
 *
 * The rule it exists to enforce (SIL-1345, verification spec section 9;
 * SIL-1371 added the third clause):
 *
 *   - `active` + Tier 1/2 + a PASSING window verdict -> the Verified mark.
 *   - `active` + Tier 3    -> NEUTRAL. A self-reported run never earns the mark,
 *                             and its label is the verbatim shared constant.
 *   - `active` + a `fail:*` window verdict, or no verdict this surface may see
 *                          -> NEUTRAL. See `showsVerifiedMark` below.
 *   - `expired` / `none`   -> NEUTRAL, with distinct labels. Both mean "no
 *                             current Active Verification"; NEITHER means a
 *                             failed run, so neither gets the failure palette.
 *
 * A FAIL is still not a state this chip RENDERS — it has no failure palette and
 * gains none here; the verdict itself is carried by the per-node verdict badge
 * in `LotContentsTable` and the pass/fail counts on the card's evidence strip.
 * What SIL-1371 changed is narrower and in one direction only: a failed run no
 * longer earns the affirmative mark. Withholding a green shield is not the same
 * as asserting a failure, and this chip still does only the former — collapsing
 * `fail` into `expired`/`none` would be exactly the conflation `avState` was
 * added to prevent.
 */
import { ShieldCheck, ShieldQuestion } from 'lucide-react';
import type { AvState } from '../../dto/marketplace.js';
import { cn } from '../utils.js';
import { verificationVerdictFamily } from '../../verification/presentation.js';
import { AV_STATE_LABELS, AV_STATE_NOTES, AV_TIER_3_LABEL } from './copy/listingDetail';

/** Muted, never red: the neutral third state must not read as a failure. */
const NEUTRAL =
  'bg-muted text-muted-foreground ring-border';
const VERIFIED = 'bg-emerald-50 text-emerald-700 ring-emerald-200';

export interface AvStateChipProps {
  avState: AvState;
  /**
   * The worst-in-window evidence tier. `3` suppresses the Verified mark even for
   * an `active` state; `null` is the server's own "tier unknown" case and is not
   * on its own a reason to withhold the mark — `windowVerdict` is.
   *
   * REQUIRED, not optional: see `showsVerifiedMark`.
   */
  evidenceTier: 1 | 2 | 3 | null;
  /** REQUIRED. See `showsVerifiedMark`. */
  windowVerdict: string | null;
  className?: string;
}

/**
 * True only when the Verified mark is earned. Exported so a caller styling a
 * larger surface around the chip (the Passport card header) makes the same
 * decision from the same function rather than re-deriving it.
 *
 * ## `windowVerdict` (SIL-1371) — why the mark is gated on the verdict
 *
 * `avState` answers "is there a current run?", NOT "did it pass".
 * `listing_av_state()` (migration 962) returns `active` for any `results_ready`
 * run with a non-null verdict inside the 90-day window, filtering on `verdict IS
 * NOT NULL` and never on the verdict's VALUE — `fail:thermal_throttle`
 * included. Gating on `avState` alone therefore badged a failed listing
 * "Verified" with a green shield, and for an unverified buyer (whose pass/fail
 * counts migration 956 nulls at the SQL layer) there was nothing beside the chip
 * to qualify it.
 *
 * Pass either a RAW `verification_run.verdict` (`fail:thermal_throttle`,
 * `flag:serial_mismatch`, `invalid_run`) or an already-normalised family:
 * `verificationVerdictFamily` is the ONE place that vocabulary lives (SIL-1366)
 * and is a fixed point on its own output, so both work and no second mapping
 * exists to drift.
 *
 * ## Both gating inputs are REQUIRED parameters, and `null` fails CLOSED
 *
 * `evidenceTier` used to be optional with a `null` default, so a call site that
 * forgot it silently KEPT the mark — which is precisely how this class of bug
 * spreads. Following `projectEvidence`'s precedent ("making it a required
 * parameter means a caller cannot forget it"), the compiler now names every call
 * site.
 *
 * And only an affirmatively PASSING family earns the mark. `null` — no verdict,
 * an `invalid_run`, a classification this vocabulary has not learned yet, or a
 * surface whose buyer tier is not permitted to see verdicts at all — withholds
 * it. The lazy call is therefore the safe one: a caller with nothing to say
 * cannot produce a green shield by saying nothing.
 */
export function showsVerifiedMark(
  avState: AvState,
  evidenceTier: 1 | 2 | 3 | null,
  windowVerdict: string | null,
): boolean {
  if (avState !== 'active') return false;
  if (evidenceTier === 3) return false;
  const family = verificationVerdictFamily(windowVerdict);
  return family === 'pass' || family === 'pass_with_observations';
}

export function AvStateChip({ avState, evidenceTier, windowVerdict, className }: AvStateChipProps) {
  const verified = showsVerifiedMark(avState, evidenceTier, windowVerdict);
  // Keyed on the TIER, not on `!verified`: since SIL-1371 an unmarked `active`
  // chip is also what a failed run and an unreadable verdict render as, and
  // labelling either of those "seller self-reported" would be a fresh false
  // claim in place of the one this issue removed. Only Tier 3 is self-reported.
  const label =
    avState === 'active' && evidenceTier === 3 ? AV_TIER_3_LABEL : AV_STATE_LABELS[avState];
  const Icon = verified ? ShieldCheck : ShieldQuestion;

  return (
    <span
      // SIL-1377: a hook for asserting the chip's PRESENCE, which its label alone
      // cannot do - a caller that handed this component an `undefined` avState
      // rendered a chip with an empty label, so a test looking for the absence of
      // the three known labels passed on a chip that should not have existed.
      // `verified` remains detected by its palette and icon, per this file's own
      // note: the mark is the visual, and a hook for it could drift from it.
      data-testid="av-state-chip"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
        verified ? VERIFIED : NEUTRAL,
        className,
      )}
    >
      <Icon className="size-3.5 shrink-0" aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * The one-line explanation that accompanies the neutral third state. Renders
 * nothing for `active`: a current run needs no disclaimer, and the sentence
 * only exists to stop a buyer reading "expired"/"none" as "this hardware
 * failed".
 */
export function AvStateNote({ avState, className }: { avState: AvState; className?: string }) {
  if (avState === 'active') return null;
  return (
    <p className={cn('text-xs text-muted-foreground', className)}>{AV_STATE_NOTES[avState]}</p>
  );
}
